import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DsSelectComponent } from '@design-system/ds-select/component/ds-select.component';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { UserRoleEnum } from '@enums/user-role.enum';
import { NgxPaginationModule } from 'ngx-pagination';
import { SharedUiModule } from '../../../../@shared/shared-ui.module';
import { FileAttachmentTagStatusClassMap } from '../../../../@shared/constants/Constant';
import { AppCategory } from '../../../../service/service-request';
import { EMPTY, Subscription } from 'rxjs';
import { VendorService } from '../../../../service/vendor.service';
import { FilesService } from '../../../../service/files.service';
import { mobileDeviceCheck } from '@helpers/mobile-check.helper';
import { FileAttachmentTagStatuses } from '@enums/file-attachment-tag.enum';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ServiceRequestFileInterface } from '@interfaces/service-request/service-request-file.interface';
import { NotificationService } from '@notification/service';
import { catchError } from 'rxjs/operators';

declare var $: any;
@Component({
  selector: 'app-view-files-modal',
  templateUrl: './view-files-modal.component.html',
  styleUrls: ['./view-files-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    DsSelectComponent,
    NgForOf,
    NgxPaginationModule,
    NgClass,
    SharedUiModule,
  ],
})
export class ViewFilesModalComponent implements OnInit, OnDestroy {
  data: {
    serviceRequestId: string;
    fromPage: string;
    selectedFile: ServiceRequestFileInterface;
    isFileEditable: boolean;
    isTagRequired: boolean;
  } = inject(MAT_DIALOG_DATA);
  protected readonly dialogRef = inject(MatDialogRef<ViewFilesModalComponent>);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly notificationService = inject(NotificationService);
  private readonly filesService = inject(FilesService);
  protected hasChanges = false;

  @ViewChild('fileInput') fileInput: ElementRef;

  user = JSON.parse(localStorage.getItem('user'));
  fileLoader = false;
  files: ServiceRequestFileInterface[] = [];
  fileTypes: string[] = [];
  editAllFile1 = false;
  searchText = '';
  isMobileDevice = mobileDeviceCheck();
  isCustomerOrVendorCoordinator = false;
  isFileSelected = false;
  isReadOnly = false;
  fileAttachmentTagOptionList: AppCategory[];
  fileAttachmentTagStatusClass = FileAttachmentTagStatusClassMap;
  sortFileType = 'fileName';
  prioritySorting = 'isPinned';
  fileNameColumn = false;
  fileCreatedByColumn = false;
  uploadedOnColumn = false;
  fileTagColumn = false;
  sortOrder = true;
  pageSize = 10;
  currentPage = 1;
  totalCount = 0;
  selectedAircraftFileType = '';
  readonly allFileTypeOption: BaseDropdownInterface = { id: '', name: 'All' };
  fileTypeOptions: BaseDropdownInterface[] = [this.allFileTypeOption];
  tagOptions: BaseDropdownInterface[] = [];
  readonly fileTypeControl = new FormControl<BaseDropdownInterface | null>(this.allFileTypeOption);
  readonly compareById = (a: BaseDropdownInterface | null, b: BaseDropdownInterface | null): boolean => (a?.id ?? null) === (b?.id ?? null);
  multipleFileUpload: any;
  path = '';
  deletedFile = [];
  oldFile = [];
  copyArr: ServiceRequestFileInterface[] = [];
  private _subscriptions: Subscription = new Subscription();

  constructor(private vendorService: VendorService) {
    if (this.data.selectedFile) {
      this.isFileSelected = true;
    }
    if (this.data.serviceRequestId && this.data.fromPage) {
      this.getFile(this.data.fromPage, this.data.serviceRequestId);
    }
    if (this.data.isTagRequired) {
      this.getFileAttachmentTags();
    }

    this.dialogRef.disableClose = true;
    this._subscriptions.add(this.dialogRef.backdropClick().subscribe(() => this.dialogRef.close(this.hasChanges)));
    this._subscriptions.add(
      this.dialogRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') this.dialogRef.close(this.hasChanges);
      }),
    );
  }

  ngOnInit(): void {
    this.isReadOnly = this.user.role === UserRoleEnum.INTERNAL_READONLY || this.user.role === UserRoleEnum.ACCOUNTING;
    this.isCustomerOrVendorCoordinator =
      this.user.role === UserRoleEnum.CUSTOMER_COORDINATOR || this.user.role === UserRoleEnum.VENDOR_COORDINATOR;
    this._subscriptions.add(
      this.fileTypeControl.valueChanges.subscribe((value) => {
        this.selectedAircraftFileType = value?.id ?? '';
        this.cd.markForCheck();
      }),
    );
  }

  onPageChange(value: number): void {
    this.currentPage = value;
    this.getFile(this.data.fromPage, this.data.serviceRequestId);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  trackByFn(index: number, item: ServiceRequestFileInterface): string {
    return item.fileId;
  }

  pinItem(item: ServiceRequestFileInterface): void {
    this.filesService.pinFile(item.fileId).subscribe(() => {
      this.hasChanges = true;
      this.currentPage = 1;
      this.getFile(this.data.fromPage, this.data.serviceRequestId);
    });
  }

  chooseFileFunction(): void {
    this.fileTypes = [...new Set(this.files.map((x) => x.fileType))] as string[];
    this.fileTypeOptions = [this.allFileTypeOption, ...this.fileTypes.map((type) => ({ id: type, name: type }))];
  }

  private tagOptionById(tagId: string | null): BaseDropdownInterface | null {
    if (!tagId) {
      return null;
    }
    return this.tagOptions.find((option) => option.id === tagId) ?? { id: tagId, name: '' };
  }

  removeFile(file: ServiceRequestFileInterface) {
    if (!file.fileId) {
      this.files = this.files.filter((f) => f !== file);
      this.fileTypes = [...new Set(this.files.map((x) => x.fileType).filter(Boolean))] as string[];
      return;
    }
    this.filesService
      .deletedFile(file.fileId)
      .pipe(
        catchError(() => {
          this.notificationService.error('Unable to delete file!');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.notificationService.success('File deleted successfully!', 'Success');
        this.hasChanges = true;
        this.getFile(this.data.fromPage, this.data.serviceRequestId);
      });
  }

  fileEdit(file: ServiceRequestFileInterface): void {
    this.copyArr = [...this.files];
    const index = this.files.findIndex((x) => x.fileId === file.fileId);
    this.files[index].isEditable = true;
    this.files[index].fileTagControl = new FormControl<BaseDropdownInterface | null>(
      this.tagOptionById(this.files[index].fileAttachmentTagId),
    );
  }

  closeDisable(file: ServiceRequestFileInterface): void {
    if (!file.fileId) {
      this.files = this.files.filter((f) => f !== file);
      this.fileTypes = [...new Set(this.files.map((x) => x.fileType).filter(Boolean))] as string[];
      if (!this.files.some((x) => x.isEditable)) {
        this.editAllFile1 = false;
      }
      return;
    }
    const index = this.files.findIndex((x) => x.fileId === file.fileId);
    this.files[index].isEditable = false;
    const arr = this.files.map((x) => x.isEditable);
    if (arr.indexOf(true) === -1) {
      this.editAllFile1 = false;
    }
  }

  closeViewFileModel(): void {
    this.files.forEach((item) => {
      item.isEditable = false;
    });
    this.editAllFile1 = false;
    this.fileTypeControl.setValue(this.allFileTypeOption);
  }

  openServiceRequestFile(id) {
    $('#' + id).click();
  }

  editAllFile(): void {
    this.copyArr = [...this.files].map((item) => ({ ...item }));
    this.files.forEach((item) => {
      item.isEditable = true;
      item.fileTagControl = new FormControl<BaseDropdownInterface | null>(this.tagOptionById(item.fileAttachmentTagId));
    });
    this.editAllFile1 = true;
  }

  closeAll(): void {
    this.files = this.copyArr;
    this.editAllFile1 = false;
  }

  multipleFileAttach(serviceRequestId: string, fileList: ServiceRequestFileInterface[]) {
    const category = this.data.fromPage;
    const fileAttachmentTagIdList: { [key: string]: string } = {};
    if (fileList.length !== 0) {
      this.path = '';
      this.multipleFileUpload = new FormData();
      fileList.forEach((item) => {
        this.path = this.path.concat(item.fileType + ',');
        this.multipleFileUpload.append(item.displayName, item);
        fileAttachmentTagIdList[item.displayName] = item.fileAttachmentTagId;
      });
      this.multipleFileUpload.append('FileAttachmentTagIds', JSON.stringify(fileAttachmentTagIdList));
      this.path = this.path.replace(/,(?=\s*$)/, '');
      this.filesService.uploadFile(this.path, category, serviceRequestId, this.multipleFileUpload).subscribe({
        next: (resp) => {
          this.files = this.files.filter((file) => file.fileId);
          this.notificationService.success('File Added Successfully!', 'Success');
          this.getFile(this.data.fromPage, this.data.serviceRequestId);
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.notificationService.error('Failed to add file! Please try again');
        },
      });
    } else {
      this.editFile();
    }
  }

  memberFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files?.length) return;

    const defaultTagId =
      this.data.fromPage === 'Catering'
        ? (this.fileAttachmentTagOptionList?.find((tag) => tag.appCategoryName === FileAttachmentTagStatuses.cateringReceipt)
            ?.appCategoryId ?? null)
        : null;

    const added = Array.from(files).map((file) => {
      const mimeSubtype = file.type?.includes('/') ? (file.type.split('/')[1] ?? '') : '';
      const fileName = (file.name ?? '').split('.')[0] ?? '';

      return Object.assign(file, {
        fileId: '',
        fileType: mimeSubtype,
        displayName: file.name ?? '',
        extension: mimeSubtype,
        fileName,
        fileAttachmentTagId: defaultTagId,
        isEditable: true,
        fileTagControl: new FormControl<BaseDropdownInterface | null>(this.tagOptionById(defaultTagId)),
      });
    });

    this.files = [...this.files, ...added] as unknown as ServiceRequestFileInterface[];
    this.fileTypes = [...new Set(this.files.map((x) => x.fileType).filter(Boolean))] as string[];
    this.fileInput.nativeElement.value = '';
  }

  submitMultipleFile(): void {
    this.files.forEach((item) => {
      if (item.fileTagControl) {
        item.fileAttachmentTagId = item.fileTagControl.value?.id ?? null;
      }
    });
    if (this.files.find((file) => file.fileAttachmentTagId === null) && this.data.isTagRequired) {
      this.notificationService.warning('Assign tag to files first!');
    } else {
      this.oldFile = [];
      const fileNameCount = new Map();
      this.files.forEach((item) => {
        const displayName = item.displayName;
        if (fileNameCount.has(displayName)) {
          const count = fileNameCount.get(displayName);
          fileNameCount.set(displayName, count + 1);

          item.displayName = `${displayName}(${count})`;
        } else {
          fileNameCount.set(displayName, 1);
        }

        item['fileType'] = item['extension'];
        if (item.fileId !== '') {
          this.oldFile.push({
            fileId: item.fileId,
            fileName: item.displayName,
            fileType: item.fileType,
            fileAttachmentTagId: item.fileAttachmentTagId,
          });
        }
      });
      const newFile = this.files.filter((x) => x.fileId === '');
      this.multipleFileAttach(this.data.serviceRequestId ?? '', newFile);
    }
  }

  editFile(): void {
    if (this.oldFile.length !== 0) {
      this.vendorService.editFile(this.oldFile).subscribe(() => {
        this.notificationService.success('File updated successfully!', 'Success');
        this.oldFile = [];
        this.getFile(this.data.fromPage, this.data.serviceRequestId);
        this.dialogRef.close(true);
      });
    }
  }

  downloadFile(file: ServiceRequestFileInterface): void {
    if (!file.fileName) return;
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.href = file.filePath;
    a.download = file.displayName;
    a.click();
    document.body.removeChild(a);
  }

  getFile(fromPage: string, serviceRequestId: string) {
    this.fileLoader = true;
    const local = (this.files ?? []).filter((x) => x.isLocal || (!x.fileId && x.isEditable));
    this.filesService.fetchFiles(fromPage, serviceRequestId, this.currentPage, this.pageSize).subscribe({
      next: (response) => {
        this.fileLoader = false;
        if (!this.data.selectedFile) {
          this.files = [...response.items, ...local];
        } else {
          this.files = [...response.items.filter((file) => file.fileId === this.data.selectedFile.fileId), ...local];
        }
        this.totalCount = response.count;
        this.cd.markForCheck();
        this.chooseFileFunction();
      },
      error: () => {
        this.fileLoader = false;
      },
    });
  }

  private getFileAttachmentTags(): void {
    this._subscriptions.add(
      this.filesService.getFileAttachmentTags().subscribe({
        next: (response) => {
          this.fileAttachmentTagOptionList = response.data;
          this.tagOptions = (response.data ?? []).map((tag) => ({ id: tag.appCategoryId, name: tag.appCategoryName }));
          this.cd.markForCheck();
        },
        error: () => {
          this.notificationService.warning('Unable to get the file attachment tag options!', 'Warning');
        },
      }),
    );
  }

  isSaveButtonVisible(): boolean {
    return this.files.length !== 0 && !this.editAllFile1 && !this.isReadOnly && this.data.isFileEditable;
  }

  isEditButtonVisible(): boolean {
    return this.files.length !== 0 && !this.editAllFile1 && !this.isFileSelected && !this.isReadOnly;
  }

  isAddButtonVisible(): boolean {
    return !this.editAllFile1 && !this.isReadOnly && this.data.isFileEditable;
  }

  protected sortColumn(fieldName: string, fieldValue: string): void {
    this.sortFileType = fieldValue;
    this.sortOrder = !this.sortOrder;
    this[fieldName] = !this[fieldName];
  }
}
